import React from 'react';
import DocumentTitle from 'react-document-title';
import { FormattedMessage } from 'react-intl';
import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import ManageCertificationsTabs from './ManageCertificationsTabs';
import CertificationsSummary from './CertificationsSummary';
import ManageCertificationModals from './ManageCertificationModals';
import CertificationNotes from './CertificationNotes';
import generalMessages from '../App/messages';
import componentMessages from './messages';
import GenerateAll59AsButton from './GenerateAll59AsButton';
import GenerateAllRDGRCerts from './GenerateAllRDGRCerts';
import {
  GROSS_RENT_CERT_TYPE,
  RD_GROSS_RENT_CERT_TYPE,
} from '../../constants/affordableCertificationTypes';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import TableLayout from '../../components/TableLayout';
import ManageCertificationsLegend from './ManageCertificationsLegend';

// Hooks
import { useManageCertifications } from './hooksV2';
import { useFlags } from 'launchdarkly-react-client-sdk';

const messages = { ...generalMessages, ...componentMessages };

const NAME = 'manage-certifications';

export const ManageCertifications = () => {
  const baseTableProps = useBaseTableProps();
  const { intl } = baseTableProps;
  const {
    affordableSetup,
    allColumnsHidden,
    columnOptions,
    completeCertificationVisibility,
    count,
    countName,
    dateState,
    disableDownloadAllButton,
    editGrossRentCertificationMutation,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    hasSelectedCertificationType,
    isLoading,
    modalProps,
    name,
    onCSVButtonClick,
    onPlanningRangeChange,
    order,
    orderBy,
    planningRangeOptions,
    propertyAffordablePrograms,
    refetchCertifications,
    rows,
    searchState,
    selectedCertificationType,
    selectedColumns,
    selectedPlanningRange,
    setSelectedCertificationType,
    signed59ADocumentType,
    signed35608DocumentType,
    sortedAndFilteredResults,
    summary,
    summaryAffordablePrograms,
    totalCount,
    uploadSignedMutation,
  } = useManageCertifications({ NAME, intl });

  const {
    grossRentEnhancements: grossRentEnhancementsFlag,
    updatedHudGrcLogic,
  } = useFlags();

  const showHudGrcInManageCertifications = updatedHudGrcLogic === false;
  const isMovedHudGrcTabSelected =
    updatedHudGrcLogic === true &&
    selectedCertificationType === GROSS_RENT_CERT_TYPE;

  const actionMap = {
    ...(showHudGrcInManageCertifications
      ? {
          [GROSS_RENT_CERT_TYPE]: (
            <GenerateAll59AsButton
              disabled={disableDownloadAllButton}
              intl={intl}
            />
          ),
        }
      : {}),
    [RD_GROSS_RENT_CERT_TYPE]: (
      <GenerateAllRDGRCerts disabled={disableDownloadAllButton} intl={intl} />
    ),
  };

  return (
    <DocumentTitle title={'Manage Certifications'}>
      <div data-testid={NAME}>
        <TableLayout
          name={name}
          childrenElementsHeight={113 + 158}
          watch={['certificationsSummary']}
        >
          {(tableHeight) => (
            <>
              <TableHeader
                title={'Manage Certifications'}
                id={'tableHeader'}
                actions={
                  selectedCertificationType
                    ? actionMap[selectedCertificationType]
                    : undefined
                }
                Tabs={
                  <ManageCertificationsTabs
                    propertyAffordablePrograms={propertyAffordablePrograms}
                    selectedCertificationType={selectedCertificationType}
                    setSelectedCertificationType={setSelectedCertificationType}
                    affordableSetup={affordableSetup}
                  />
                }
                count={hasSelectedCertificationType ? count : undefined}
                countName={countName}
                totalCount={totalCount}
              />
              {grossRentEnhancementsFlag && showHudGrcInManageCertifications ? (
                <ManageCertificationsLegend
                  certificationType={selectedCertificationType}
                />
              ) : null}
              <CertificationsSummary
                propertyAffordablePrograms={
                  selectedCertificationType === 'RECERT'
                    ? summaryAffordablePrograms
                    : propertyAffordablePrograms
                }
                summary={summary}
                selectedCertificationType={selectedCertificationType}
                planningRangeOptions={planningRangeOptions}
                selectedPlanningRange={selectedPlanningRange}
                onPlanningRangeChange={onPlanningRangeChange}
                isLoading={isLoading}
                completeCertificationVisibility={
                  completeCertificationVisibility
                }
              />
              {isMovedHudGrcTabSelected ? null : (
                <CertificationNotes
                  sortedAndFilteredResults={sortedAndFilteredResults}
                  selectedCertificationType={selectedCertificationType}
                />
              )}
              <Table
                {...baseTableProps}
                allColumnsHidden={allColumnsHidden}
                columnOptions={columnOptions}
                dateState={dateState}
                emptyStateMessage={
                  !hasSelectedCertificationType ? (
                    <FormattedMessage {...messages.selectOption} />
                  ) : undefined
                }
                filterState={filterState}
                filterTypeState={filterTypeState}
                handleColumnChange={handleColumnChange}
                handleDateSubmit={handleDateSubmit}
                handleFilterChange={handleFilterChange}
                handleFilterTypeChange={handleFilterTypeChange}
                handleSearchSubmit={handleSearchSubmit}
                handleSortChange={handleSortChange}
                headers={filteredHeaders}
                height={tableHeight}
                isLoading={isLoading}
                name={name}
                onCSVButtonClick={
                  hasSelectedCertificationType && count > 0
                    ? onCSVButtonClick
                    : undefined
                }
                order={order}
                orderBy={orderBy}
                rows={rows}
                count={count}
                searchState={searchState}
                selectedColumns={selectedColumns}
                totalCount={totalCount}
              />
            </>
          )}
        </TableLayout>
        <ManageCertificationModals
          {...modalProps}
          signed59ADocumentType={signed59ADocumentType}
          signed35608DocumentType={signed35608DocumentType}
          uploadSignedMutation={uploadSignedMutation}
          editGrossRentCertificationMutation={
            editGrossRentCertificationMutation
          }
          refetchCertifications={refetchCertifications}
          propertyAffordablePrograms={propertyAffordablePrograms}
        />
      </div>
    </DocumentTitle>
  );
};

export default ManageCertifications;
